const THOUSANDS_GROUP_LENGTH = 3;

// Large enough to be grouped in every locale; Spanish leaves four-digit numbers ungrouped
const GROUPED_SAMPLE_NUMBER = 1000000;

/**
 * Parses a number typed into a free-text field, accepting either '.' or ',' as the decimal separator.
 * Stripping every comma, as a plain cleanup would, silently turns "0,35" into 35.
 *
 * - Spaces and currency or unit text are ignored ("R 1 234,50" → 1234.5).
 * - With both separators present, the last one is the decimal point ("1.234,5" and "1,234.5" → 1234.5).
 * - A separator repeated more than once groups thousands ("1,234,567" → 1234567).
 * - A single separator is a decimal point, unless it is the language's group separator followed by exactly
 *   three digits ("1,234" in English → 1234, "1,234" in Spanish → 1.234).
 *
 * @param text - The raw input value
 * @param language - The active UI language, used to read its group separator
 * @returns The parsed number, or null when the text is empty or not a number
 */
export function parseLocaleNumber(text: string | null | undefined, language: string): number | null {
  let normalized = (text ?? '').replace(/[^\d.,-]/g, '');

  const lastDot = normalized.lastIndexOf('.');
  const lastComma = normalized.lastIndexOf(',');

  if (lastDot !== -1 && lastComma !== -1) {
    const decimalSeparator = lastDot > lastComma ? '.' : ',';
    const groupSeparator = decimalSeparator === '.' ? ',' : '.';
    normalized = normalized.split(groupSeparator).join('').replace(decimalSeparator, '.');
  } else if (lastDot !== -1 || lastComma !== -1) {
    const separator = lastDot !== -1 ? '.' : ',';
    const occurrences = normalized.split(separator).length - 1;
    const digitsAfter = normalized.length - normalized.lastIndexOf(separator) - 1;
    const isGrouping = occurrences > 1
      || (separator === getGroupSeparator(language) && digitsAfter === THOUSANDS_GROUP_LENGTH);

    normalized = isGrouping ? normalized.split(separator).join('') : normalized.replace(separator, '.');
  }

  return /^-?\d+(\.\d+)?$/.test(normalized) ? Number(normalized) : null;
}

function getGroupSeparator(language: string): string {
  try {
    return new Intl.NumberFormat(language).formatToParts(GROUPED_SAMPLE_NUMBER).find(part => part.type === 'group')?.value ?? ',';
  } catch {
    return ',';
  }
}
